import type { FloorplanTransform } from '@/types';

export interface Size { width: number; height: number }

const MARGIN = 0.95; // 5% safety margin on contain-fit

/**
 * Fit image in frame with margin. Auto-rotate 90° when orientation mismatches
 * (portrait image + landscape frame, or vice versa). Square images never rotate.
 */
export function computeInitialTransform(image: Size, frame: Size): FloorplanTransform {
  const imgLandscape = image.width > image.height;
  const imgPortrait = image.height > image.width;
  const frameLandscape = frame.width > frame.height;
  const framePortrait = frame.height > frame.width;

  const mismatch =
    (imgPortrait && frameLandscape) || (imgLandscape && framePortrait);
  const rotation = mismatch ? 90 : 0;

  // After rotation, effective image dimensions swap
  const effW = rotation === 0 ? image.width : image.height;
  const effH = rotation === 0 ? image.height : image.width;

  // Contain-fit scale: ratio of frame-edge to image-edge, taking the more constrained
  const scale = Math.min(frame.width / effW, frame.height / effH) * MARGIN;

  return { rotation, scale, tx: 0.5, ty: 0.5 };
}

/**
 * Snap rotation to nearest multiple of 90° within ±5° threshold, otherwise return as-is.
 * Always normalizes to (-180, 180].
 */
export function snapRotation(degrees: number): number {
  const normalized = ((degrees + 180) % 360 + 360) % 360 - 180;
  const snapPoints = [-180, -90, 0, 90, 180];
  for (const snap of snapPoints) {
    if (Math.abs(normalized - snap) <= 5) return snap === -180 ? 180 : snap;
  }
  return normalized;
}

/**
 * Render an image into a canvas context, applying the transform and filling
 * empty frame area with white. The context's canvas size must match the frame.
 */
export function applyTransformToCanvas(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  transform: FloorplanTransform,
  frame: Size
): void {
  // White background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, frame.width, frame.height);

  const { rotation, scale, tx, ty } = transform;

  ctx.save();
  // Move origin to the target center (tx, ty are 0..1 frame-relative)
  ctx.translate(frame.width * tx, frame.height * ty);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.scale(scale, scale);
  // Draw image centered at origin
  ctx.drawImage(image, -image.width / 2, -image.height / 2);
  ctx.restore();
}
